import React, { useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import RecipientPicker from "@/components/messages/RecipientPicker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useGetOwnPhoneNumbersQuery, useSendMessageMutation, useGetSmsEligibilityQuery } from "@/store/rest_api";
import OutboundGuardDialogs from "@/components/compliance/OutboundGuardDialogs";
import { useOutboundGuard } from "@/hooks/useOutboundGuard";
import { isSmsSendable, isTollFree } from "@/util/a2p";
import { TOLL_FREE_TEXT_CLASS } from "@/components/phone-numbers/TollFreeBadge";
import SmsBlockedNotice from "@/components/messages/SmsBlockedNotice";
import DoNotTextNotice from "@/components/messages/DoNotTextNotice";
import { useTextBlockingEntry } from "@/hooks/useTextBlockingEntry";
import { Loader2 } from "lucide-react";

interface NewConversationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const NewConversationDialog: React.FC<NewConversationDialogProps> = ({
  open,
  onOpenChange,
}) => {
  const { data: phoneNumbers = [] } = useGetOwnPhoneNumbersQuery();
  const [sendMessage, { isLoading }] = useSendMessageMutation();
  const outboundGuard = useOutboundGuard();
  const { data: smsEligibility } = useGetSmsEligibilityQuery();
  const orgApproved = !!smsEligibility?.orgApproved;

  const [toNumber, setToNumber] = useState("");
  const [fromPhoneNumberId, setFromPhoneNumberId] = useState("");
  const [body, setBody] = useState("");
  const textBlockingEntry = useTextBlockingEntry([toNumber]);

  const activeNumbers = phoneNumbers.filter((pn) => pn.active);
  const selectedFromNumber = activeNumbers.find((pn) => pn.id === fromPhoneNumberId);

  const handleSend = async () => {
    if (!toNumber.trim() || !fromPhoneNumberId) return;

    try {
      await outboundGuard.guard(async (options) => {
        await sendMessage({
          fromPhoneNumber: { id: fromPhoneNumberId },
          toNumbers: [toNumber.trim()],
          body: body.trim() || undefined,
          ...options,
        }).unwrap();

        setToNumber("");
        setBody("");
        onOpenChange(false);
      });
    } catch (e: any) {
      toast.error(e?.detail || "Failed to send message");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <OutboundGuardDialogs guard={outboundGuard} actionLabel="Send anyway" />
        <DialogHeader>
          <DialogTitle>New Conversation</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <SmsBlockedNotice fromPhoneNumber={selectedFromNumber} />
          <div className="space-y-2">
            <Label>To</Label>
            <RecipientPicker value={toNumber} onChange={setToNumber} />
          </div>
          <DoNotTextNotice blockingEntry={textBlockingEntry} />
          <div className="space-y-2">
            <Label>From</Label>
            <Select value={fromPhoneNumberId} onValueChange={setFromPhoneNumberId}>
              <SelectTrigger>
                <SelectValue placeholder="Select a number" />
              </SelectTrigger>
              <SelectContent>
                {activeNumbers.map((pn) => {
                  const sendable = isSmsSendable(pn, orgApproved);
                  return (
                    <SelectItem
                      key={pn.id}
                      value={pn.id}
                      disabled={!sendable}
                      className={isTollFree(pn) ? TOLL_FREE_TEXT_CLASS : undefined}
                    >
                      {pn.alias ? `${pn.alias} (${pn.number})` : pn.number}
                      {!sendable ? (isTollFree(pn) ? " (not verified)" : " (not enrolled)") : ""}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Message</Label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Type your message..."
              rows={3}
              className="w-full resize-none bg-background border border-input rounded-md px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={isLoading || !toNumber.trim() || !fromPhoneNumberId || !!textBlockingEntry}
          >
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NewConversationDialog;
